import styled from 'styled-components';
import { Box, Typography } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MuiButton from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { MINT_MIST } from '../../constants/brandSurfaces';

/** Brand tokens */
const blue = '#0d4efe';
const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';
const headingFont = "'Plus Jakarta Sans', sans-serif";

/* ══════════════════════════════════════════════════
   Header — Mint mist panel with photo, name, ratings, contact
   ══════════════════════════════════════════════════ */
export const HeroSection = styled.section`
  margin: 12px 0 8px;
  padding: 32px 36px;
  border-radius: 24px;
  background: ${MINT_MIST};
  border: 1px solid rgba(26, 26, 46, 0.06);

  @media (max-width: 768px) {
    padding: 24px 20px;
  }

  @media (max-width: 600px) {
    padding: 22px 16px;
    border-radius: 18px;
  }
`;

export const HeroContent = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 16px;
  }

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

export const BusinessPhoto = styled(Box)`
  width: 112px;
  height: 112px;
  border-radius: 18px;
  overflow: hidden;
  border: 4px solid #ffffff;
  background: #ffffff;
  flex-shrink: 0;
  box-shadow: 0 6px 20px rgba(26, 26, 46, 0.1);

  @media (max-width: 768px) {
    width: 88px;
    height: 88px;
    border-radius: 16px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const BusinessInfo = styled(Box)`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

export const TitleRow = styled(Box)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 10px;
  }
`;

export const BusinessTitle = styled.h1`
  margin: 0 0 6px;
  font-family: ${headingFont};
  font-size: 2.2rem;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: ${ink};

  @media (max-width: 600px) {
    font-size: 1.65rem;
  }
`;

export const BusinessLocation = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1rem;
  font-weight: 500;
  color: ${muted};

  svg {
    color: ${blue};
  }
`;

export const HeroDetailsRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

/** One contact chip (address, email, phone). */
export const InfoItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
  padding: 6px 14px 6px 6px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid rgba(26, 26, 46, 0.08);
  font-size: 0.92rem;
  font-weight: 500;
  color: ${ink};

  svg {
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    padding: 5px;
    border-radius: 50%;
    color: ${blue};
    background: rgba(1, 235, 178, 0.18);
  }
`;

export const ContactLink = styled.a`
  color: inherit;
  text-decoration: none;
  overflow-wrap: anywhere;

  &:hover {
    color: ${blue};
  }

  &:focus-visible {
    outline: 2px solid ${blue};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const StorefrontEmptyRatingText = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid rgba(26, 26, 46, 0.08);
  color: ${muted};
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
`;

export const StorefrontRatingsColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;

  @media (max-width: 900px) {
    align-items: flex-start;
  }
`;

export const StorefrontRatingsActions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    flex-wrap: wrap;
    align-items: center;
  }
`;

export const HeroReviewButton = styled.button`
  padding: 8px 18px;
  border-radius: 10px;
  border: 1.5px solid ${ink};
  background: #ffffff;
  color: ${ink};
  font-family: inherit;
  font-size: 0.92rem;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover,
  &:focus-visible {
    border-color: ${blue};
    color: ${blue};
  }
`;

/* ══════════════════════════════════════════════════
   Services section — grouped catalog (trade → group → cards)
   ══════════════════════════════════════════════════ */
export const ServicesSection = styled.section`
  margin-top: 36px;

  @media (max-width: 600px) {
    margin-top: 28px;
  }
`;

export const SectionTitle = styled.h2`
  margin: 0 0 6px;
  font-family: ${headingFont};
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
  color: ${ink};

  @media (max-width: 600px) {
    font-size: 1.4rem;
  }
`;

export const SectionSubtitle = styled.p`
  margin: 0 0 18px;
  font-size: 1rem;
  line-height: 1.55;
  color: ${muted};
`;

export const TradeJumpChips = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 24px;
`;

export const TradeJumpChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px 7px 8px;
  border-radius: 999px;
  border: 1px solid rgba(26, 26, 46, 0.08);
  background: #ffffff;
  color: ${ink};
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  .trade-jump-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: ${(p) => `color-mix(in srgb, ${p.$accent || blue} 12%, #ffffff)`};
    color: ${(p) => p.$accent || blue};
  }

  svg {
    font-size: 16px;
  }

  &:hover {
    border-color: ${(p) => `color-mix(in srgb, ${p.$accent || blue} 45%, transparent)`};
    box-shadow: 0 4px 14px rgba(26, 26, 46, 0.06);
  }

  &:focus-visible {
    outline: 2px solid ${(p) => p.$accent || blue};
    outline-offset: 2px;
  }
`;

export const TradeSection = styled(Box)`
  margin-bottom: 20px;
  padding: 8px;
  border-radius: 22px;
  background: ${MINT_MIST};
  border: 1px solid rgba(26, 26, 46, 0.06);
  scroll-margin-top: 88px;

  &:last-child {
    margin-bottom: 8px;
  }

  @media (max-width: 600px) {
    padding: 6px;
    border-radius: 18px;
  }
`;

export const TradeSectionHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 0;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid rgba(26, 26, 46, 0.06);
  background: #ffffff;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${(p) => `color-mix(in srgb, ${p.$accent || blue} 40%, transparent)`};
    box-shadow: 0 4px 14px rgba(26, 26, 46, 0.05);
  }

  &:focus-visible {
    outline: 2px solid ${(p) => p.$accent || blue};
    outline-offset: 2px;
  }
`;

export const TradeSectionHeaderText = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const TradeSectionToggle = styled.span`
  flex-shrink: 0;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: ${muted};
  transition: transform 0.2s ease, background 0.15s ease, color 0.15s ease;
  transform: rotate(${(p) => (p.$expanded ? '0deg' : '-90deg')});

  svg {
    font-size: 22px;
  }

  ${TradeSectionHeader}:hover & {
    background: rgba(13, 78, 254, 0.06);
    color: ${ink};
  }
`;

export const TradeSectionBody = styled(Box)`
  padding: 18px 10px 12px;

  @media (max-width: 600px) {
    padding: 14px 4px 8px;
  }
`;

export const TradeSectionIcon = styled.span`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => `color-mix(in srgb, ${p.$accent || blue} 12%, #ffffff)`};
  color: ${(p) => p.$accent || blue};

  svg {
    font-size: 22px;
  }
`;

export const TradeSectionTitle = styled(Typography).attrs({ component: 'span' })`
  && {
    display: block;
    margin: 0;
    font-family: inherit;
    font-weight: 700;
    font-size: 1.08rem;
    color: ${ink};
    line-height: 1.3;
  }
`;

export const TradeSectionCount = styled(Typography).attrs({ component: 'span' })`
  && {
    display: block;
    margin-top: 2px;
    font-family: inherit;
    font-size: 0.86rem;
    font-weight: 500;
    color: ${muted};
  }
`;

export const ServiceGroupBlock = styled(Box)`
  margin-bottom: 22px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const ServiceGroupLabel = styled.h3`
  margin: 0 0 12px 4px;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${ink};
`;

export const ServiceCardsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;

  > .MuiCard-root {
    max-width: none;
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const EmptyServices = styled.div`
  padding: 36px 24px;
  border-radius: 20px;
  background: ${MINT_MIST};
  border: 1px dashed rgba(26, 26, 46, 0.14);
  text-align: center;

  h3 {
    margin: 0 0 6px;
    font-size: 1.1rem;
    font-weight: 700;
    color: ${ink};
  }

  p {
    margin: 0;
    font-size: 0.96rem;
    color: ${muted};
  }
`;

/* ══════════════════════════════════════════════════
   Review CTA card (between services & reviews)
   ══════════════════════════════════════════════════ */
export const ReviewCTACard = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin: 32px 0 16px;
  padding: 22px 26px;
  border-radius: 20px;
  background: ${MINT_MIST};
  border: 1px solid rgba(26, 26, 46, 0.06);

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px 18px;
    gap: 14px;
  }
`;

export const ReviewCTAText = styled.div`
  flex: 1;
  min-width: 0;

  h2 {
    margin: 0 0 4px;
    font-family: ${headingFont};
    font-size: 1.15rem;
    font-weight: 700;
    color: ${ink};
  }

  p {
    margin: 0;
    font-size: 0.96rem;
    line-height: 1.5;
    color: ${muted};
  }
`;

export const ReviewCTAButton = styled.button`
  flex-shrink: 0;
  padding: 11px 24px;
  border: none;
  border-radius: 10px;
  font-family: inherit;
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
  color: #ffffff;
  background: linear-gradient(90deg, ${teal} 0%, ${blue} 100%);
  box-shadow: 0 4px 14px rgba(13, 78, 254, 0.35);
  transition: filter 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;

  &:hover {
    filter: brightness(1.05);
    box-shadow: 0 6px 18px rgba(13, 78, 254, 0.4);
  }
`;

/* ══════════════════════════════════════════════════
   Floating review button (sticky bottom-left)
   ══════════════════════════════════════════════════ */
export const FloatingReviewButton = styled.button`
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 20px;
  border: none;
  border-radius: 50px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  color: #ffffff;
  background: linear-gradient(135deg, ${teal} 0%, ${blue} 100%);
  box-shadow: 0 4px 20px rgba(13, 78, 254, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 6px 28px rgba(13, 78, 254, 0.4);
  }

  &:active {
    transform: translateY(0) scale(1);
  }

  @media (max-width: 600px) {
    bottom: 16px;
    left: 16px;
    padding: 10px 16px;
    font-size: 0.82rem;
  }
`;

/* ══════════════════════════════════════════════════
   Existing review dialog (signed-in user already reviewed)
   ══════════════════════════════════════════════════ */
export const StyledExistingReviewDialog = muiStyled(Dialog)(() => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(26, 26, 46, 0.52)',
    backdropFilter: 'blur(6px)',
  },
  '& .MuiDialog-paper': {
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid rgba(13, 78, 254, 0.14)',
    boxShadow:
      '0 24px 56px rgba(13, 78, 254, 0.16), 0 8px 24px rgba(0, 0, 0, 0.06)',
    background: '#ffffff',
    maxWidth: 520,
    width: '100%',
  },
}));

export const ExistingReviewDialogTitle = muiStyled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.75),
  margin: 0,
  padding: theme.spacing(2.5, 3),
  paddingRight: theme.spacing(6),
  position: 'relative',
  background:
    'linear-gradient(135deg, rgba(1, 235, 178, 0.14) 0%, rgba(13, 78, 254, 0.1) 55%, rgba(13, 78, 254, 0.04) 100%)',
  borderBottom: '1px solid rgba(13, 78, 254, 0.1)',
  '& .existing-review-icon-wrap': {
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background:
      'linear-gradient(145deg, rgba(1, 235, 178, 0.2) 0%, rgba(13, 78, 254, 0.15) 100%)',
    border: '1px solid rgba(13, 78, 254, 0.18)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.6)',
    marginTop: 2,
  },
  '& .existing-review-icon': {
    fontSize: 26,
    color: blue,
  },
  '& .existing-review-title-block': {
    flex: 1,
    minWidth: 0,
    paddingTop: theme.spacing(0.25),
  },
  '& .existing-review-heading': {
    margin: 0,
    fontWeight: 700,
    fontSize: '1.125rem',
    lineHeight: 1.35,
    letterSpacing: '-0.02em',
    color: ink,
  },
  '& .existing-review-subheading': {
    margin: 0,
    marginTop: theme.spacing(0.75),
    fontSize: '0.875rem',
    lineHeight: 1.55,
    color: muted,
    fontWeight: 400,
  },
}));

export const ExistingReviewCloseButton = muiStyled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: muted,
  borderRadius: 10,
  '&:hover': {
    background: 'rgba(13, 78, 254, 0.08)',
    color: ink,
  },
}));

export const ExistingReviewDialogContent = muiStyled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  paddingTop: theme.spacing(2.25),
  '&.MuiDialogContent-root': {
    paddingTop: theme.spacing(2.25),
  },
}));

export const ExistingReviewPanel = muiStyled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2, 2.25),
  borderRadius: 12,
  background: '#f7fafc',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.8)',
}));

export const ExistingReviewMetaRow = muiStyled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
}));

export const ExistingReviewDialogActions = muiStyled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 3, 2.5),
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  background: 'linear-gradient(180deg, rgba(247, 250, 252, 0.95) 0%, #f0f4f8 100%)',
  borderTop: '1px solid rgba(13, 78, 254, 0.1)',
}));

export const ExistingReviewSecondaryButton = muiStyled(MuiButton)(() => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 10,
  padding: '10px 20px',
  color: ink,
  borderColor: 'rgba(13, 78, 254, 0.28)',
  backgroundColor: '#fff',
  borderWidth: '1.5px',
  '&:hover': {
    borderColor: blue,
    backgroundColor: 'rgba(13, 78, 254, 0.06)',
    borderWidth: '1.5px',
  },
}));

export const ExistingReviewRejectionNotice = muiStyled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2, 2.25),
  borderRadius: 12,
  background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(13, 78, 254, 0.06) 100%)',
  border: '1px solid rgba(245, 124, 0, 0.35)',
  borderLeft: `4px solid ${blue}`,
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.75)',
}));

export const ExistingReviewPrimaryButton = muiStyled(MuiButton)(() => ({
  textTransform: 'none',
  borderRadius: 10,
  fontWeight: 700,
  padding: '10px 22px',
  color: '#ffffff',
  background: 'linear-gradient(90deg, #01ebb2 0%, #0d4efe 100%)',
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  '&:hover': {
    background: 'linear-gradient(90deg, #02dfc0 0%, #0a45e8 100%)',
    boxShadow: '0 6px 18px rgba(13, 78, 254, 0.4)',
  },
}));